import { useEffect, useState } from 'react';
import { isSupported as isFirebaseMessagingSupported } from 'firebase/messaging';
import { useNotificationSocket } from '../shared/pusNotification/socket/useNotificationSocket';
import { usePushNotifications } from '../shared/pusNotification/fcm/usePushNotifications';
import { NOTIFICATION_FEATURE_FLAGS } from '../shared/pusNotification/notificationFeatureFlags';
import { useForegroundPushToasts } from '../shared/pusNotification/useForegroundPushToasts';
import {
  getInitialFcmOperationalState,
  subscribeFcmOperationalChanges,
} from '../shared/pusNotification/fcm/fcmChannelHealth';
import {
  canUseWebPush,
  resolvePushPermissionState,
  subscribePushPermissionChanges,
} from '../shared/pusNotification/fcm/pushPermission';
import { scheduleFcmSyncWithRetry } from '../shared/pusNotification/fcm/fcmSyncCoordinator';
import { FCM_SYNC_REASONS } from '../shared/pusNotification/fcm/pushConstants';
import { canSyncPushNotifications } from '../shared/pusNotification/fcm/pushEligibility';
import { AUTH_TOKEN_KEYS } from '../shared/constants/common';
import userService from '../shared/services/userService';
import { isUserLoggedIn } from '../shared/utils/function';

const FCM_SYNC_OPTIONS = {
  isUserEligible: canSyncPushNotifications,
  updateFcmToken: userService.updateFcmToken,
};

const isAccessTokenSetEvent = (event) =>
  event?.detail?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN &&
  event?.detail?.action === 'set';

/** Ensures FCM token sync runs even if other hooks mount late or hot-reload skips them. */
const useFcmTokenBootstrap = () => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    if (!NOTIFICATION_FEATURE_FLAGS.fcm || !canUseWebPush()) {
      return undefined;
    }

    const requestSync = (reason) => {
      if (!isUserLoggedIn()) {
        return;
      }
      scheduleFcmSyncWithRetry(FCM_SYNC_OPTIONS, { reason });
    };

    if (isUserLoggedIn()) {
      requestSync(FCM_SYNC_REASONS.BOOT);
    }

    const onAuthChange = (event) => {
      if (isAccessTokenSetEvent(event)) {
        requestSync(FCM_SYNC_REASONS.AUTH);
      }
    };

    const onCrossTabStorage = (event) => {
      if (event?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && event.newValue) {
        requestSync(FCM_SYNC_REASONS.AUTH);
      }
    };

    window.addEventListener('localStorageChange', onAuthChange);
    window.addEventListener('storage', onCrossTabStorage);

    return () => {
      window.removeEventListener('localStorageChange', onAuthChange);
      window.removeEventListener('storage', onCrossTabStorage);
    };
  }, []);
};

const canTryFcm = () => NOTIFICATION_FEATURE_FLAGS.fcm && canUseWebPush();

const useNotificationChannelPriority = () => {
  const [isFirebaseSupported, setIsFirebaseSupported] = useState(true);
  const [pushPermission, setPushPermission] = useState('default');
  const [fcmOperational, setFcmOperational] = useState(getInitialFcmOperationalState);

  useEffect(() => {
    let isMounted = true;

    const refreshChannelState = async () => {
      if (!canTryFcm()) {
        if (isMounted) {
          setIsFirebaseSupported(false);
          setPushPermission('unsupported');
        }
        return;
      }

      const permission = await resolvePushPermissionState();
      if (!isMounted) {
        return;
      }
      setPushPermission(permission);

      try {
        const supported = await isFirebaseMessagingSupported();
        if (isMounted) {
          setIsFirebaseSupported(Boolean(supported));
        }
      } catch {
        if (isMounted) {
          setIsFirebaseSupported(false);
        }
      }
    };

    refreshChannelState();
    const removePermissionSubscription = subscribePushPermissionChanges(refreshChannelState);
    const removeOperationalSubscription = subscribeFcmOperationalChanges((state) => {
      if (isMounted) {
        setFcmOperational(state);
      }
    });

    window.addEventListener('focus', refreshChannelState);
    document.addEventListener('visibilitychange', refreshChannelState);

    return () => {
      isMounted = false;
      removePermissionSubscription();
      removeOperationalSubscription();
      window.removeEventListener('focus', refreshChannelState);
      document.removeEventListener('visibilitychange', refreshChannelState);
    };
  }, []);

  const fcmPreferred =
    canTryFcm() && pushPermission === 'granted' && isFirebaseSupported;
  const fcmDelivering = fcmPreferred && fcmOperational === true;

  return {
    fcmPreferred,
    fcmDelivering,
    socketIo: NOTIFICATION_FEATURE_FLAGS.socketIo,
  };
};

/**
 * App-level realtime notification bootstrap (FCM for browser push, Socket.IO for realtime fallback).
 * Mounted once under App; renders no UI.
 *
 * Channel priority:
 * - FCM bootstrap always runs when the browser supports web push (handles login/logout sync).
 * - FCM delivers foreground toasts when permission is granted and a token is active.
 * - Socket.IO delivers foreground toasts when FCM is unavailable or has not obtained a token yet.
 * - Dedup on the shared bus prevents double toasts if both channels fire.
 */
const NotificationInitializer = () => {
  const notificationChannels = useNotificationChannelPriority();

  useFcmTokenBootstrap();
  useForegroundPushToasts({
    enabled: notificationChannels.fcmPreferred || notificationChannels.socketIo,
  });
  usePushNotifications({ enabled: true });
  useNotificationSocket({
    enabled: notificationChannels.socketIo,
    dispatchForegroundToasts: !notificationChannels.fcmDelivering,
  });
  return null;
};

export default NotificationInitializer;
